import { useState, useCallback, useRef, useEffect } from 'react';
import { getToken, fetchUserInfo as fetchUserInfoFromAuth, clearUserInfoCache } from '../utils/auth';
import { authenticatedFetch, isRateLimited, getRateLimitRemaining } from '../utils/auth';

const ORDER_HISTORY_CACHE_TTL = 60 * 1000;

function getOrderHistoryCache(cacheKeyBase) {
  try {
    const cached = localStorage.getItem(cacheKeyBase);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > ORDER_HISTORY_CACHE_TTL) {
      localStorage.removeItem(cacheKeyBase);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setOrderHistoryCache(cacheKeyBase, data) {
  try {
    localStorage.setItem(cacheKeyBase, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    /* ignore */
  }
}

export function useOrderHistory(user) {
  const [orderHistory, setOrderHistory] = useState([]);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [orderHistoryRateLimited, setOrderHistoryRateLimited] = useState(false);
  const [orderHistoryRateLimitUntil, setOrderHistoryRateLimitUntil] = useState(null);
  const [dateFilter, setDateFilter] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const orderHistoryRateLimitRef = useRef({ limited: false, until: null });

  useEffect(() => {
    orderHistoryRateLimitRef.current = {
      limited: orderHistoryRateLimited,
      until: orderHistoryRateLimitUntil,
    };
  }, [orderHistoryRateLimited, orderHistoryRateLimitUntil]);

  const fetchOrderHistory = useCallback(
    async (allUsers = false, overrides = {}) => {
      const effectiveDateFilter = overrides.dateFilter ?? dateFilter;
      const effectiveCustomStart = overrides.customStartDate ?? customStartDate;
      const effectiveCustomEnd = overrides.customEndDate ?? customEndDate;
      const cacheKeyBase = `order_history_${effectiveDateFilter}_${effectiveCustomStart}_${effectiveCustomEnd}_${allUsers ? 'all' : 'user'}`;

      if (isRateLimited()) {
        const cached = getOrderHistoryCache(cacheKeyBase);
        if (cached) setOrderHistory(cached);
        return;
      }

      const rateLimitState = orderHistoryRateLimitRef.current;
      if (rateLimitState.limited && rateLimitState.until && Date.now() < rateLimitState.until) {
        const cached = getOrderHistoryCache(cacheKeyBase);
        if (cached) setOrderHistory(cached);
        return;
      }

      const cached = getOrderHistoryCache(cacheKeyBase);
      if (cached) setOrderHistory(cached);

      setOrderHistoryLoading(true);
      try {
        const isAdmin = user?.email === 'begite@naver.com';
        let url = '/api/portfolio/history';
        const params = [];
        if (effectiveDateFilter === 'today') params.push('days=1');
        else if (effectiveDateFilter === 'week') params.push('days=7');
        else if (effectiveDateFilter === 'month') params.push('days=30');
        else if (effectiveDateFilter === 'custom' && effectiveCustomStart && effectiveCustomEnd) {
          params.push(`startDate=${effectiveCustomStart}`, `endDate=${effectiveCustomEnd}`);
        }
        if (isAdmin && allUsers) params.push('allUsers=true');
        const urlParams = new URLSearchParams(window.location.search);
        const page = parseInt(urlParams.get('page') || '1', 10);
        const limit = Math.min(100, Math.max(1, parseInt(urlParams.get('limit') || '100', 10)));
        params.push(`page=${page}`, `limit=${limit}`);
        if (params.length) url += '?' + params.join('&');

        const response = await authenticatedFetch(url, { timeout: 60000 });
        if (response.ok) {
          const data = await response.json();
          const trades = data.trades || [];
          setOrderHistory(trades);
          setOrderHistoryCache(cacheKeyBase, trades);
          setOrderHistoryRateLimited(false);
          setOrderHistoryRateLimitUntil(null);
        } else if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const waitTime = retryAfter ? parseInt(retryAfter, 10) * 1000 : 15 * 60 * 1000;
          setOrderHistoryRateLimited(true);
          setOrderHistoryRateLimitUntil(Date.now() + waitTime);
          const cachedOn429 = getOrderHistoryCache(cacheKeyBase);
          if (cachedOn429) setOrderHistory(cachedOn429);
        } else if (response.status === 502 || response.status === 503) {
          const cachedErr = getOrderHistoryCache(cacheKeyBase);
          if (cachedErr) setOrderHistory(cachedErr);
        }
      } catch (error) {
        const cachedErr = getOrderHistoryCache(cacheKeyBase);
        if (cachedErr) setOrderHistory(cachedErr);
        console.error('주문 내역 조회 실패:', error);
      } finally {
        setOrderHistoryLoading(false);
      }
    },
    [dateFilter, customStartDate, customEndDate, user]
  );

  return {
    orderHistory,
    orderHistoryLoading,
    orderHistoryRateLimited,
    orderHistoryRateLimitUntil,
    dateFilter,
    setDateFilter,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    fetchOrderHistory,
  };
}

export function useTradingAuth() {
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const loadUser = useCallback(async (force = false) => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setAuthError(true);
      return null;
    }
    try {
      const userData = await fetchUserInfoFromAuth(force);
      if (userData) {
        setUser(userData);
        setAuthError(false);
        return userData;
      }
      setAuthError(true);
      return null;
    } catch {
      setAuthError(true);
      return null;
    }
  }, []);

  const handleLoginSuccess = useCallback((userData) => {
    setUser(userData);
    setAuthError(false);
    setShowLogin(false);
    clearUserInfoCache();
  }, []);

  return {
    user,
    setUser,
    authError,
    setAuthError,
    showLogin,
    setShowLogin,
    loadUser,
    handleLoginSuccess,
  };
}
